import {useState, useEffect, useCallback, useRef} from 'react'
import listBank from '../../auth/components/server/Steps9'
import {BankOption, BankAccount} from '../types'

export interface UseBankAccountsProps {
  setActiveStep: (active: boolean) => void
  handleGetListBank: (banks: BankOption[]) => void
  handleGetListBankBr: (branches: BankOption[][]) => void
  setFieldValue: (field: string, value: any) => void
}

export const useBankAccounts = ({
  setActiveStep,
  handleGetListBank,
  handleGetListBankBr,
  setFieldValue,
}: UseBankAccountsProps) => {
  const [selectedOptions, setSelectedOptions] = useState<BankAccount[]>([{}])
  const [valueListBank, setValueListBank] = useState<BankOption[]>([])
  const [valueListBranch, setValueListBranch] = useState<BankOption[][]>([[]])
  const [removeButtons, setRemoveButtons] = useState<boolean[]>([false])
  const [isLoadingBanks, setIsLoadingBanks] = useState<boolean>(false)
  const [loadingBranches, setLoadingBranches] = useState<Set<number>>(new Set())

  // Use refs to store callback functions to avoid dependency issues
  const handleGetListBankRef = useRef(handleGetListBank)
  const handleGetListBankBrRef = useRef(handleGetListBankBr)
  const setActiveStepRef = useRef(setActiveStep)
  const setFieldValueRef = useRef(setFieldValue)

  // Update refs when props change
  useEffect(() => {
    handleGetListBankRef.current = handleGetListBank
    handleGetListBankBrRef.current = handleGetListBankBr
    setActiveStepRef.current = setActiveStep
    setFieldValueRef.current = setFieldValue
  })

  const DEFAULT_BANK_OPTION = ''
  const DEFAULT_BRANCH_OPTION = ''
  const MAX_BANK_ACCOUNTS = 3

  // Fetch initial bank list - only run once on mount
  useEffect(() => {
    let isMounted = true

    const fetchBankList = async () => {
      if (valueListBank.length > 0) return // Prevent duplicate calls

      setIsLoadingBanks(true)
      try {
        const listBankRes: any = await listBank({code_tp: 'bank_cd_off'})
        const bankList = listBankRes.data_list || []

        if (isMounted) {
          setValueListBank(bankList)
          handleGetListBankRef.current(bankList)
        }
      } catch (error) {
        console.error('Error fetching bank list:', error)
      } finally {
        if (isMounted) {
          setIsLoadingBanks(false)
        }
      }
    }

    fetchBankList()

    return () => {
      isMounted = false
    }
  }, [valueListBank.length]) // Only depend on length to prevent initial duplicate calls

  // Handle select change for bank and branch
  const handleSelectChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>, fieldName: string, index: number) => {
      const value = event.target.value

      // Update selected options
      setSelectedOptions((prevSelectedOptions) => {
        const newSelectedOptions = [...prevSelectedOptions]
        newSelectedOptions[index] = {...newSelectedOptions[index], [fieldName]: value}

        const hasBranchSelected = Boolean(newSelectedOptions[index][`bank_brch_cd_${index + 1}`])

        if (hasBranchSelected) {
          setActiveStepRef.current(false)
        }

        return newSelectedOptions
      })

      // Fetch branch list when bank is selected
      if (fieldName.startsWith('bank_cd_off') && value) {
        // A branch belongs to its selected bank. Clear the previous selection before
        // loading the branches for the newly selected bank.
        setFieldValueRef.current(`bank_brch_cd_${index + 1}`, '')
        setSelectedOptions((prevSelectedOptions) => {
          const newSelectedOptions = [...prevSelectedOptions]
          newSelectedOptions[index] = {...newSelectedOptions[index]}
          delete newSelectedOptions[index][`bank_brch_cd_${index + 1}`]
          return newSelectedOptions
        })
        setValueListBranch((prevState) => {
          const newState = [...prevState]
          newState[index] = []
          return newState
        })

        // Prevent duplicate API calls for the same bank at the same index
        setLoadingBranches((prev) => new Set(prev).add(index))

        try {
          const listBankBranch: any = await listBank({
            code_tp: 'bank_brch',
            bank_code: value,
          })

          setValueListBranch((prevState) => {
            const newState = [...prevState]
            newState[index] = listBankBranch.data_list || []
            handleGetListBankBrRef.current(newState)
            return newState
          })
        } catch (error) {
          console.error('Error fetching bank branches:', error)
        } finally {
          setLoadingBranches((prev) => {
            const newSet = new Set(prev)
            newSet.delete(index)
            return newSet
          })
        }
      }
    },
    [] // No dependencies needed since we use refs and functional updates
  )

  // Add new bank account
  const handleAddBank = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()

      setSelectedOptions((prev) => {
        if (prev.length >= MAX_BANK_ACCOUNTS) return prev
        return [...prev, {}]
      })

      setValueListBranch((prev) => [...prev, []])
      setRemoveButtons((prev) => [...prev, true])
      setActiveStepRef.current(true)
    },
    [] // No dependencies needed since we use functional updates and refs
  )

  // Remove bank account
  const handleRemoveBank = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, index: number) => {
      event.preventDefault()

      // Check if we can remove (need at least one account)
      setSelectedOptions((prevSelectedOptions) => {
        if (prevSelectedOptions.length <= 1) return prevSelectedOptions

        // Clear form fields for removed accounts
        const fieldsToReset = [
          `bank_cd_off_${index + 1}`,
          `bank_brch_cd_${index + 1}`,
          `bank_acnt_no_${index + 1}`,
        ]

        fieldsToReset.forEach((field) => setFieldValueRef.current(field, undefined))

        // Update other state arrays
        setValueListBranch((prev) => prev.filter((_, i) => i !== index))
        setRemoveButtons((prev) => prev.filter((_, i) => i !== index))
        setActiveStepRef.current(false)

        return prevSelectedOptions.filter((_, i) => i !== index)
      })
    },
    [] // No dependencies needed since we use functional updates and refs
  )

  return {
    selectedOptions,
    valueListBank,
    valueListBranch,
    removeButtons,
    isLoadingBanks,
    loadingBranches,
    DEFAULT_BANK_OPTION,
    DEFAULT_BRANCH_OPTION,
    MAX_BANK_ACCOUNTS,
    handleSelectChange,
    handleAddBank,
    handleRemoveBank,
  }
}
